import * as React from 'react';
import type { ICommand } from '../../../core/commands/ICommand';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import { useMasterChartChangeIntercept } from '../components/useMasterChartChangeIntercept';
import { useLiveChartScenarioGate } from './useLiveChartScenarioGate';
import { planningScenarioSeedFromCommand } from '../services/planningScenarioSeed';
import { useAtlasUnifiedChanges } from './useAtlasUnifiedChanges';
import { useAtlasCanvasDraftCommands } from './useAtlasCanvasDraftCommands';
import { atlasDraftRequiresProtectedAuthoring } from '../services/atlasDraftAuthoringMode';

/**
 * Live-chart launchers and derived view maintenance opt out of history. Every
 * history-bearing command is an authored canvas/model mutation and must enter
 * a draft before CommandManager is allowed to execute it.
 */
function isLiveChartMutationCommand(command: ICommand): boolean {
  return command.executionScope !== 'presentation' && command.skipHistory !== true;
}

/**
 * One guard for every CommandManager mutation path on a live Atlas chart:
 * property panels, plus handles, drag/drop, connectors, keyboard actions, and
 * toolbar tools. Menus/pickers remain free to open because their launcher
 * commands are explicitly history-free.
 */
export function useLiveChartCommandGuard(input: {
  commandManager: CommandManager;
  diagramId: string | null | undefined;
  folderId?: string | null;
  isLiveChart: boolean;
  /** A planning chart always records authored edits into its own draft. */
  isPlanningChart?: boolean;
  hasOpenDraft: boolean;
}): React.ReactElement | null {
  const { commandManager, diagramId, isLiveChart, isPlanningChart = false, hasOpenDraft } = input;
  const unified = useAtlasUnifiedChanges();
  const prepareCommand = useAtlasCanvasDraftCommands({ folderId: input.folderId, diagramId,
    enabled: isLiveChart || isPlanningChart, planning: isPlanningChart });
  const { ensureScenarioForEdit } = useLiveChartScenarioGate({
    diagramId,
    forceScenario: isPlanningChart,
  });
  const hasOpenDraftNow = React.useCallback(
    () => hasOpenDraft || useScenariosStore.getState().activeScenarioId != null,
    [hasOpenDraft],
  );
  const { guardChange, interceptDialog } = useMasterChartChangeIntercept({
    diagramId,
    // A planning chart is already the separate-plan destination, so it must
    // create its draft silently instead of showing the master-chart choice.
    isLiveChart: isLiveChart && !isPlanningChart,
    hasOpenDraft: hasOpenDraftNow,
  });

  React.useEffect(() => {
    if (!isLiveChart && !isPlanningChart) return undefined;

    return commandManager.setExecutionGuard((command) => {
      if (!isLiveChartMutationCommand(command)) return true;
      // Protected planning commands already carry their server-backed draft
      // port. Starting the generic canvas scenario here creates a second,
      // empty authority that survives after the protected head is applied.
      if (command.mutationDestination === 'protected_plan') return true;
      // An open protected draft (succession, staffing) takes later canvas
      // edits too, even without the unified flag; a legacy edit cannot join it.
      if (unified || atlasDraftRequiresProtectedAuthoring(diagramId)) {
        // New protected authoring never starts or writes a client/Yjs scenario.
        // Legacy input stays separate until explicitly adopted in review.
        if (useScenariosStore.getState().activeScenarioId) {
          throw new Error('Review or discard the existing canvas draft before starting a saved organization draft.');
        }
        return prepareCommand(command);
      }

      const planningScenario = planningScenarioSeedFromCommand(command);

      return new Promise<boolean>((resolve) => {
        guardChange(
          () => resolve(ensureScenarioForEdit()),
          () => resolve(false),
          planningScenario
            ? { scenario: planningScenario }
            : { planningUnavailableReason: 'This change cannot be represented safely in a planning chart.' },
        );
      });
    });
  }, [commandManager, diagramId, ensureScenarioForEdit, guardChange, isLiveChart, isPlanningChart, prepareCommand, unified]);

  return interceptDialog;
}
